import {
    FIELD_TO_PROFILE_FIELD,
    VALID_FIELDS,
    BUSINESS_HOURS_TYPE_MAIN,
    ROW_STATUS,
    FIELD_CONFIG,
} from 'directory/listings/notification-modals/constants';
import { LocationDataRow, FieldType, MissingFieldData } from 'directory/listings/notification-modals/types';
import { Profile } from 'directory/profiles/Profiles';

const transformPhoneData = (phoneData: any): string | null => {
    if (Array.isArray(phoneData) && phoneData.length > 0) {
        const firstPhone = phoneData[0];

        if (firstPhone?.phone && typeof firstPhone.phone === 'string' && firstPhone.phone.trim()) {
            return firstPhone.phone;
        }
    }
    return null;
};

const transformWebsiteData = (websiteData: any): string | null => {
    if (Array.isArray(websiteData) && websiteData.length > 0) {
        const firstWebsite = websiteData[0];

        if (firstWebsite?.url && typeof firstWebsite.url === 'string' && firstWebsite.url.trim()) {
            return firstWebsite.url;
        }
    }
    return null;
};

const formatTimeRange = (timeRange: any): string => {
    const { openHour, openMinutes, closeHour, closeMinutes } = timeRange;
    const formatTime = (hour: number, minutes: number) =>
        `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    return `${formatTime(openHour, openMinutes)} - ${formatTime(closeHour, closeMinutes)}`;
};

const transformBusinessHoursData = (businessHoursData: any): any[] | null => {
    if (!Array.isArray(businessHoursData) || businessHoursData.length === 0) {
        return null;
    }

    const mainHours = businessHoursData.find((bh: any) => bh.type === BUSINESS_HOURS_TYPE_MAIN);

    if (!mainHours?.dailyHours || !Array.isArray(mainHours.dailyHours) || mainHours.dailyHours.length === 0) {
        return null;
    }

    const transformedHours = mainHours.dailyHours
        .map((dh: any) => {
            if (!dh.day) return null;

            let hours = 'Closed';

            if (!dh.closed && dh.timeRanges?.[0]) {
                const timeRange = dh.timeRanges[0];

                if (
                    typeof timeRange.openHour === 'number' &&
                    typeof timeRange.openMinutes === 'number' &&
                    typeof timeRange.closeHour === 'number' &&
                    typeof timeRange.closeMinutes === 'number'
                ) {
                    hours = formatTimeRange(timeRange);
                }
            }

            return { day: dh.day, hours };
        })
        .filter(Boolean);

    return transformedHours.length > 0 ? transformedHours : null;
};

const transformDescriptionData = (descriptionData: any): string | null => {
    if (typeof descriptionData === 'string' && descriptionData.trim()) {
        return descriptionData;
    }
    return null;
};

const getLocationName = (locationUid: number, profiles: Profile[]): string => {
    const profile = profiles.find((p) => p.locationUid === locationUid);

    return profile?.internalName || `Location ${locationUid}`;
};

const FIELD_TRANSFORMERS: Record<string, (data: any) => string | any[] | null> = {
    __profile_phone__: transformPhoneData,
    __profile_website__: transformWebsiteData,
    __profile_business_hr__: transformBusinessHoursData,
    __profile_description__: transformDescriptionData,
};

export const transformMissingFieldDataToRows = (
    missingData: MissingFieldData[],
    profiles: Profile[],
): LocationDataRow[] => {
    const validFields = Object.values(FIELD_TO_PROFILE_FIELD).filter(Boolean);

    const filteredData = missingData.filter((data) => validFields.includes(data.field as any));

    return filteredData.map((data, index) => {
        const transformer = FIELD_TRANSFORMERS[data.field];
        const googleData = transformer ? transformer(data.collected) : null;

        const fieldEntry = Object.entries(FIELD_TO_PROFILE_FIELD).find(([, value]) => value === data.field);
        const field = (fieldEntry?.[0] as FieldType) || 'UNKNOWN';
        const fieldConfig = FIELD_CONFIG[field];

        return {
            id: `${data.locationUid}-${index}`,
            locationUid: data.locationUid,
            locationName: getLocationName(data.locationUid, profiles),
            googleData,
            reputationData: null,
            fieldType: fieldConfig.type,
            field,
            fieldIcon: fieldConfig.icon,
            status: ROW_STATUS.MISSING,
        } as LocationDataRow;
    });
};

export const extractFieldFromNotification = (parameters: { fields?: string[] }): FieldType => {
    if (!parameters.fields || parameters.fields.length === 0) {
        return 'UNKNOWN';
    }

    const field = parameters.fields[0] as FieldType;

    return (VALID_FIELDS as readonly string[]).includes(field) ? field : 'UNKNOWN';
};
